import { ListItem, ListItemButton, type ListItemProps, ListItemText } from "@mui/material";
import { MdsCheck, MdsChevronRight } from "@sinch/icons";
import { isArray } from "ramda-adjunct";
import { forwardRef, Ref, type RefAttributes } from "react";
import { InlinedItems } from "@sinch/components/form/MultilevelSelect/InlinedItems";
import { ListColumnProps, SearchResultIndices } from "@sinch/components/form/MultilevelSelect/types";
import { MdsIcon } from "@sinch/components/MdsIcon";

interface ListItemWrapperProps<TValue = any> {
  /** The value associated with this list item */
  value: TValue;
  /** The label to display, can be a single index or an array of indices */
  label: SearchResultIndices | SearchResultIndices[];
  /** Handler for mouse over events */
  onMouseOver?: () => void;
  /** Handler for click events */
  onClick: ListColumnProps<TValue>["onChange"];
  /** Whether the item is in hover state */
  hoverState?: boolean;
  /** Whether the item is selected */
  selected?: boolean;
  /** Additional props to pass to the MUI ListItem component */
  listItemProps?: ListItemProps;
  hasChildren?: boolean;
}

/**
 * Base implementation of the list item wrapper.
 * Renders a list item with appropriate styling for selection state.
 */
const ListItemWrapperBase = <TValue,>(
  { value, label, onMouseOver, onClick, selected, listItemProps, hasChildren }: ListItemWrapperProps<TValue>,
  ref: Ref<HTMLLIElement>
) => (
  <ListItem ref={ref} disablePadding onClick={(e) => onClick?.(e, value)} onMouseOver={onMouseOver} {...listItemProps}>
    <ListItemButton selected={selected}>
      <ListItemText primary={isArray(label) ? <InlinedItems label={label as SearchResultIndices[]} /> : label} />
      {selected && <MdsIcon icon={MdsCheck} />}
      {hasChildren && <MdsIcon fontSize="small" icon={MdsChevronRight} sx={{ color: "grey.600" }} />}
    </ListItemButton>
  </ListItem>
);

export const ListItemWrapper = forwardRef(ListItemWrapperBase) as <TValue>(
  props: ListItemWrapperProps<TValue> & RefAttributes<HTMLLIElement>
) => ReturnType<typeof ListItemWrapperBase>;
